import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import CLOSE from "../../../../assets/images/icons/close.svg";
import Button from "../../../buttons/buttons";
import Input from "../../../inputs/inputs";

const emptyRow = () => ({ to: "", from: "", charge: "" });

export default function PricePerMeterRangeModal({ ranges, setRanges }) {
  const [isOpen, setIsOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [rows, setRows] = useState(
    ranges?.length > 0 ? ranges : [emptyRow()]
  );

  function openModal() {
    setRows(ranges?.length > 0 ? ranges : [emptyRow()]);
    setValidationErrors([]);
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  const handleRowChange = (index, e) => {
    const { name, value } = e.target;
    const updated = rows.map((row, i) =>
      i === index ? { ...row, [name]: value } : row
    );
    setValidationErrors([]);
    setRows(updated);
  };

  const addRow = () => {
    setValidationErrors([]);
    setRows([...rows, emptyRow()]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) return;
    setValidationErrors([]);
    setRows(rows.filter((_, i) => i !== index));
  };

  const validate = () => {
    const errors = [];

    // First range must start at 0
    if (Number(rows[0].to) !== 0) {
      errors.push("The first range must start at 0.");
    }

    // Check for overlaps: sort by `to` then verify each range's `from` <= next range's `to`
    const sorted = [...rows].sort((a, b) => Number(a.to) - Number(b.to));
    for (let i = 0; i < sorted.length - 1; i++) {
      const currentEnd = Number(sorted[i].from);
      const nextStart = Number(sorted[i + 1].to);
      if (nextStart < currentEnd) {
        errors.push(
          `Ranges overlap: ${sorted[i].to}–${sorted[i].from} and ${sorted[i + 1].to}–${sorted[i + 1].from}.`
        );
      }
    }

    return errors;
  };

  const handleSave = () => {
    const errors = validate();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);
    setRanges(rows);
    closeModal();
  };

  return (
    <>
      <button
        type="button"
        className="border border-dashed border-[#C2C9D1] text-center p-2 w-full"
        onClick={openModal}
      >
        {ranges?.length > 0
          ? `${ranges.length} range${ranges.length > 1 ? "s" : ""} attached`
          : "--Attach--"}
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full sm:w-[629px] bg-white dark:bg-[#121212] rounded-sm py-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8">
                  <div className="sm:block absolute top-4 right-5">
                    <button
                      type="button"
                      className="bg-[#ffeae5] rounded-md p-1 sm:p-3"
                      onClick={closeModal}
                    >
                      <span className="sr-only">Close</span>
                      <img src={CLOSE} alt="close" className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="sm:px-8 px-4">
                    <Dialog.Title
                      as="p"
                      className="text-md font-medium text-[#717485] dark:text-white"
                    >
                      Price Per Meter Range
                    </Dialog.Title>
                  </div>

                  <div className="border-b-2 mt-4" />

                  <div className="mt-5 px-4 sm:px-8">
                    {rows.map((row, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-2 gap-3 mb-6 items-end"
                      >
                        <Input.Label
                          title={"Start (meters)"}
                          htmlFor={`to_${index}`}
                          name="to"
                          type="number"
                          placeholder={"e.g. 0"}
                          value={row.to}
                          onChange={(e) => handleRowChange(index, e)}
                        />
                        <Input.Label
                          title={"End (meters)"}
                          htmlFor={`from_${index}`}
                          name="from"
                          type="number"
                          placeholder={"e.g. 1000"}
                          value={row.from}
                          onChange={(e) => handleRowChange(index, e)}
                        />

                        <div className="flex items-end gap-2 col-span-2">
                          <div className="flex-1">
                            <Input.Label
                              title={"Price"}
                              htmlFor={`charge_${index}`}
                              name="charge"
                              type="number"
                              placeholder={"e.g. 500"}
                              value={row.charge}
                              onChange={(e) => handleRowChange(index, e)}
                            />
                          </div>
                          {rows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRow(index)}
                              className="mb-[18px] text-primary text-xl font-bold leading-none"
                              aria-label="Remove row"
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={addRow}
                        className="text-primary text-sm mt-1 mb-6"
                      >
                        Add more +
                      </button>
                    </div>


                    {validationErrors.length > 0 && (
                      <ul className="mb-4 text-xs text-primary space-y-1">
                        {validationErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    )}

                    <div className="flex justify-between mt-4 mb-3">
                      <Button.Secondary
                        title={"Cancel"}
                        className={"rounded-md px-4"}
                        onClick={closeModal}
                      />
                      <Button.Primary
                        title={"Save Ranges"}
                        className={"rounded-md px-4"}
                        type="button"
                        onClick={handleSave}
                      />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
